import React, { useState } from "react";
import GoogleIcon from "../../assets/Icon.png";
import LazyImage from "../shared/LazyImage";
import {
  sendResetPasswordCode,
  verifyResetPasswordOtp,
  setNewPassword as setNewPasswordApi,
} from "../../utils/api";
import BackgroundVideo from "../shared/BackgroundVideo";
import { VIDEO_URLS } from "../../utils/videoConfig";
import { trackForgotPasswordCompleted } from "../../utils/analytics";

const ForgotPassword = ({ onContinue, onBack }) => {
  const [step, setStep] = useState("forgot");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage("");
      onBack();
    }, 2000);
  };

  const handleSendCode = async () => {
    if (!email) return setError("Please check your email");
    setLoading(true);
    try {
      await sendResetPasswordCode(email);
      setError(" ");
      setStep("verify");
    } catch (err) {
      setError("Please check your email");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = otp.join("");
    if (code.length !== 5) return setError("Enter 5-digit code");
    setLoading(true);
    try {
      const token = await verifyResetPasswordOtp(email, code);
      setToken(token);
      setError(" ");
      setStep("newPassword");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) return setError("Enter new password");
    if (confirmPassword !== newPassword)
      return setError("Passwords do not match");

    setLoading(true);
    setError("");
    try {
      await setNewPasswordApi(token, newPassword);
      trackForgotPasswordCompleted();
      showToast("Password updated successfully!");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < otp.length - 1) {
        const nextInput = document.querySelector(
          `.otp-inputs input:nth-child(${index + 2})`
        );
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.querySelector(
        `.otp-inputs input:nth-child(${index})`
      );
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedData) return;

    const newOtp = [...otp];
    for (let i = 0; i < otp.length; i++) {
      newOtp[i] = pastedData[i] || "";
    }
    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length, otp.length) - 1;
    const nextInput = document.querySelector(
      `.otp-inputs input:nth-child(${lastFilledIndex + 1})`
    );
    if (nextInput) nextInput.focus();
  };

  return (
    <div className="forgot-password-container">
      <BackgroundVideo className="bgVideoStep11" src={VIDEO_URLS.video4} />

      {step === "forgot" && (
        <div className="forgot-step">
          <div className="forgotTop">
            <div className="forgotTexts">
              <h2 className="forgotTitle">Forgot your password?</h2>
              <h4 className="forgotSubtitle">
                Enter your email, then verify the code we send you.
              </h4>
            </div>
            <input
              type="email"
              placeholder="Enter your email"
              className="cardStep15Input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <div className="error-message-input">{error}</div>}
          </div>
          <div className="forgot-buttons">
            <button
              className="forgotBtn"
              onClick={handleSendCode}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send code"}
            </button>
            <div className="forgotSecBtn" onClick={() => onContinue(17)}>
              Back to Log In
            </div>
          </div>
        </div>
      )}

      {step === "verify" && (
        <div className="forgot-step">
          <div className="forgotTexts">
            <h2 className="forgotTitle">Verify your code</h2>
            <h4 className="forgotSubtitle">
              Enter the 4-digit code we sent to your email to continue.
            </h4>
          </div>
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                className="verifyCodeInput"
                placeholder="x"
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
              />
            ))}
          </div>{" "}
          {error && <div className="error-message-input">{error}</div>}
          <div className="forgot-buttons">
            <button
              className="forgotBtn"
              onClick={handleVerifyCode}
              disabled={loading}
            >
              {loading ? "Verifying..." : "Apply Code"}
            </button>
            <div className="forgotSecBtn" onClick={handleSendCode}>
              Resend Code
            </div>
          </div>
        </div>
      )}

      {step === "newPassword" && (
        <div className="forgot-step">
          <div className="forgotTexts">
            <h2 className="forgotTitle">Set your new password</h2>
            <h4 className="forgotSubtitle">Enter a strong new password.</h4>
          </div>
          <div className="cardStep15InputWrap">
            <div className="inputWrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />{" "}
              <button
                type="button"
                className="cardStep15InputSvg"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                      stroke="#9B9CAA"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                      stroke="#9B9CAA"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                      stroke="#9B9CAA"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            <div className="inputWrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Сonfirm password"
                className="form-input confirm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />{" "}
              <button
                type="button"
                className="cardStep15InputSvg"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
                aria-label={
                  showConfirmPassword ? "Hide password" : "Show password"
                }
              >
                {showConfirmPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                      stroke="#9B9CAA"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                      stroke="#9B9CAA"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                      stroke="#9B9CAA"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && <div className="error-message-input">{error}</div>}
          <button
            className="forgotBtn"
            onClick={handleUpdatePassword}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Password"}
          </button>
        </div>
      )}

      {step === "newPassword" && toastMessage && (
        <div className="toast-notification">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={3}
            stroke="white"
            className="w-5 h-5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;
